
/**
 * Hook for retrieving the current user's profile and roles using React Query.
 * This hook is typically used to access authentication and authorization data throughout the application.
 *
 * @param {boolean} enabled - Determines whether the query should execute. Useful for conditional fetching.
 * @returns {UseQueryResult<UserProfileResponse, unknown>} React Query result object containing user profile and roles.
 *
 * @example
 * // Example: Fetch and display the current user's name
 * import { useUserProfile } from '../hooks/useUserProfile';
 *
 * // The hook returns all TanStack React Query state variables:
 * // data, isLoading, error, isError, isPending, isSuccess, isFetching, refetch, etc.
 *
 * const {
 *   data,
 *   isLoading,
 *   error,
 *   isError,
 *   isPending,
 *   isSuccess,
 *   isFetching,
 *   refetch
 * } = useUserProfile(true);

 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { type UserRole } from '../models/UserRoles'
import { UserManager } from '../APIManager'
import { CURRENT_ROLE_ID_KEY } from '../consts/roles'
import { ON_CLASSRANKED } from '../consts'
import * as Sentry from '@sentry/react'
import ROUTES from '../consts/paths'
import { useNavigate } from 'react-router-dom'
export const USER_PROFILE_KEY = ['userProfile']

export interface UserProfileResponse {
  user: any
  roles: UserRole[]
  isCustomBrandingEnabled?: boolean
}

// Helper to set current role ID in localStorage if not present
const ensureCurrentRoleId = (roles: any): void => {
  const hasRoleId = localStorage.getItem(CURRENT_ROLE_ID_KEY)
  if (!hasRoleId && Array.isArray(roles) && roles[0]?.roleId) {
    localStorage.setItem(CURRENT_ROLE_ID_KEY, roles[0].roleId)
  }
}

// Helper to set Sentry user context if on ClassRanked
const setSentryUserContext = (user: any): void => {
  if (!user || typeof user !== 'object' || !('payload' in user)) return
  const { email, name, studentId } = user.payload || {}
  if (!email) return
  Sentry.setUser({
    id: studentId ?? email,
    email,
    username: name
  })
  if (studentId) {
    Sentry.setTag('schoolId', studentId)
  }
}

// Helper to check if user has no roles and should be redirected
const shouldNavigatePermissionDenied = (roles: any): boolean => {
  return Array.isArray(roles) && roles.length === 0
}

export const useUserProfile = (
  enabled = false
): UseQueryResult<UserProfileResponse, unknown> => {
  const navigate = useNavigate()
  return useQuery<UserProfileResponse>({
    queryKey: USER_PROFILE_KEY,
    queryFn: async (): Promise<UserProfileResponse> => {
      const response = await UserManager.getUserProfile()
      ensureCurrentRoleId(response?.data?.roles)

      if (shouldNavigatePermissionDenied(response?.data?.roles)) {
        navigate(ROUTES.PERMISSION_DENIED)
      }

      if (ON_CLASSRANKED && response?.data?.user) {
        setSentryUserContext(response.data.user)
      }

      return response.data as UserProfileResponse
    },

    enabled,
    retry: false
  })
}
